import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, Plus, Trash2, Truck } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import LrReceiptPrint from './LrReceiptPrint';

const EMPTY_PKG = { packages: '', description: '', actual_weight: '', charged_weight: '', rate: '', amount: '', sort_order: 1 };
const EMPTY_FORM = {
  job_no: '', service_tax_payable_by: 'Consignor', insurance_taken_by: 'Consignor',
  data_logger_no: '', date: '', time: '', temp: '', consignment_note_no: '', consignment_note_date: '',
  vehicle_no: '', delivery_at: '', from_location: '', to_location: '',
  consignor_name_address: '', consignee_name_address: '',
  l_time: '', l_date: '', ul_time: '', ul_date: '', remarks: '',
  packages: [{ ...EMPTY_PKG }],
};

const LrReceiptForm = () => {
  const { docketId, lrId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isEdit = !!lrId;
  const isPrintRoute = location.pathname.endsWith('/print');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [savedId, setSavedId] = useState(lrId || null);
  const [docketNo, setDocketNo] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const auth = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' }), []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [dRes, locRes] = await Promise.all([
          fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: auth() }),
          fetch(`${backendUrl}/api/locations?limit=500`, { headers: auth() }),
        ]);
        if (dRes.ok) {
          const d = await dRes.json();
          setDocketNo(d.system_docket_no || docketId);
          const cid = d.company_id || '';
          setCompanyId(cid);
          if (cid) {
            const cRes = await fetch(`${backendUrl}/api/companies/${cid}`, { headers: auth() });
            if (cRes.ok) { const c = await cRes.json(); setCompanyName(c.name || ''); }
          }
        }
        if (locRes.ok) {
          const ld = await locRes.json();
          setLocations(Array.isArray(ld) ? ld : (ld.items || []));
        }
        if (isEdit) {
          const rRes = await fetch(`${backendUrl}/api/lr-receipts/${lrId}`, { headers: auth() });
          if (!rRes.ok) throw new Error('Failed to load LR Receipt');
          const r = await rRes.json();
          setFormData({ ...r, packages: r.packages?.length ? r.packages : [{ ...EMPTY_PKG }], date: r.date || '', consignment_note_date: r.consignment_note_date || '', l_date: r.l_date || '', ul_date: r.ul_date || '' });
          setSavedId(r.id);
        } else {
          const pRes = await fetch(`${backendUrl}/api/lr-receipts/prefill-data?docket_id=${docketId}`, { headers: auth() });
          if (pRes.ok) {
            const p = await pRes.json();
            setFormData(prev => ({ ...prev, ...p, packages: p.packages?.length ? p.packages : [{ ...EMPTY_PKG }] }));
          }
        }
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, [docketId, lrId, isEdit, backendUrl, auth, toast]);

  useEffect(() => { if (isPrintRoute && !loading) setShowPrint(true); }, [isPrintRoute, loading]);

  const set = (f, v) => setFormData(prev => ({ ...prev, [f]: v }));
  const updatePkg = (idx, f, v) => setFormData(prev => ({ ...prev, packages: prev.packages.map((p, i) => i === idx ? { ...p, [f]: v } : p) }));
  const addPkg = () => setFormData(prev => ({ ...prev, packages: [...prev.packages, { ...EMPTY_PKG, sort_order: prev.packages.length + 1 }] }));
  const removePkg = (idx) => { if (formData.packages.length <= 1) return; setFormData(prev => ({ ...prev, packages: prev.packages.filter((_, i) => i !== idx) })); };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = { ...formData, docket_id: docketId, company_id: companyId, date: formData.date || null, consignment_note_date: formData.consignment_note_date || null, l_date: formData.l_date || null, ul_date: formData.ul_date || null, packages: formData.packages.map((p, i) => ({ ...p, sort_order: i + 1 })) };
      const url = isEdit ? `${backendUrl}/api/lr-receipts/${savedId}` : `${backendUrl}/api/lr-receipts`;
      const res = await fetch(url, { method: isEdit ? 'PUT' : 'POST', headers: auth(), body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      const saved = await res.json();
      setSavedId(saved.id);
      toast({ title: 'Saved', description: 'LR Receipt saved successfully' });
      if (!isEdit) navigate(`/dockets/${docketId}/lr-receipts/${saved.id}/edit`, { replace: true });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (showPrint) {
    return (
      <div className="min-h-screen bg-white">
        <div className="print:hidden fixed top-4 left-4 right-4 z-50 flex justify-between">
          <Button variant="outline" onClick={() => setShowPrint(false)} className="bg-white shadow-md"><ArrowLeft className="w-4 h-4 mr-2" />Back to Form</Button>
          <Button onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700 shadow-md"><Printer className="w-4 h-4 mr-2" />Print / Save PDF</Button>
        </div>
        <div className="pt-16"><LrReceiptPrint formData={formData} docketNo={docketNo} companyName={companyName} /></div>
      </div>
    );
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  const F = ({ label, field, type = 'text', readOnly = false, placeholder = '' }) => (
    <div>
      <Label className="text-xs font-medium text-gray-500">{label}</Label>
      <Input type={type} value={formData[field] || ''} onChange={e => set(field, e.target.value)} readOnly={readOnly} placeholder={placeholder} className={`mt-1 h-9 text-sm ${readOnly ? 'bg-gray-50' : ''}`} data-testid={`lr-${field}`} />
    </div>
  );

  const LocSel = ({ label, field }) => (
    <div>
      <Label className="text-xs font-medium text-gray-500">{label}</Label>
      <select value={formData[field] || ''} onChange={e => set(field, e.target.value)} className="mt-1 h-9 w-full text-sm border border-gray-200 rounded-md px-2 bg-white" data-testid={`lr-${field}`}>
        <option value="">-- Select --</option>
        {locations.map(l => <option key={l.id} value={l.location_name}>{l.location_name}</option>)}
      </select>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/dockets/${docketId}/lr-receipts`)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Truck className="w-5 h-5 text-orange-500" />{isEdit ? 'Edit LR Receipt' : 'New LR Receipt'}</h1>
              <p className="text-xs text-gray-500">Docket: {docketNo}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {savedId && (
              <Button variant="outline" onClick={() => setShowPrint(true)} className="border-orange-600 text-orange-600 hover:bg-orange-50" data-testid="lr-print-btn"><Printer className="w-4 h-4 mr-2" />Print</Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="lr-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Receipt Info */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-3">
            <h2 className="text-sm font-semibold text-white">Receipt Info</h2>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <F label="Job No." field="job_no" readOnly />
              <F label="Date" field="date" type="date" />
              <F label="Time" field="time" placeholder="e.g. 10:30 AM" />
              <F label="Temp" field="temp" placeholder="e.g. 2-8°C" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-500">Service Tax Payable By</Label>
                <div className="flex items-center gap-4 mt-1.5">
                  {['Consignor', 'Consignee', 'Transport'].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <input type="radio" name="stp" value={opt} checked={formData.service_tax_payable_by === opt} onChange={() => set('service_tax_payable_by', opt)} className="accent-orange-500" data-testid={`lr-stp-${opt.toLowerCase()}`} />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500">Insurance will be taken By</Label>
                <div className="flex items-center gap-4 mt-1.5">
                  {['Consignor', 'Consignee'].map(opt => (
                    <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <input type="radio" name="ins" value={opt} checked={formData.insurance_taken_by === opt} onChange={() => set('insurance_taken_by', opt)} className="accent-orange-500" data-testid={`lr-ins-${opt.toLowerCase()}`} />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
              <F label="Data Logger No." field="data_logger_no" placeholder="Logger number" />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <F label="Consignment Note No." field="consignment_note_no" placeholder="CN Number" />
              <F label="Consignment Note Date" field="consignment_note_date" type="date" />
              <F label="Vehicle No." field="vehicle_no" placeholder="e.g. GJ01AB1234" />
              <LocSel label="Delivery At" field="delivery_at" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <LocSel label="From" field="from_location" />
              <LocSel label="To" field="to_location" />
              <div>
                <Label className="text-xs font-medium text-gray-500">Remarks</Label>
                <Textarea value={formData.remarks || ''} onChange={e => set('remarks', e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Remarks" data-testid="lr-remarks" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-gray-500">Consignor Name &amp; Address</Label>
                <Textarea value={formData.consignor_name_address || ''} onChange={e => set('consignor_name_address', e.target.value)} rows={4} className="mt-1 text-sm" placeholder="Consignor name and full address" data-testid="lr-consignor" />
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500">Consignee Name &amp; Address</Label>
                <Textarea value={formData.consignee_name_address || ''} onChange={e => set('consignee_name_address', e.target.value)} rows={4} className="mt-1 text-sm" placeholder="Consignee name and full address" data-testid="lr-consignee" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <F label="Loading Time" field="l_time" placeholder="L Time" />
              <F label="Loading Date" field="l_date" type="date" />
              <F label="Unloading Time" field="ul_time" placeholder="UL Time" />
              <F label="Unloading Date" field="ul_date" type="date" />
            </div>
          </div>
        </Card>

        {/* Package Info */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Package Info</h2>
            <span className="text-xs text-orange-200">{formData.packages.length} row(s)</span>
          </div>
          <div className="p-5 space-y-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 w-20">Packages</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 w-28">Actual Wt</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 w-28">Charged Wt</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 w-24">Rate</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 w-28">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.packages.map((p, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-1 py-1"><Input value={p.packages || ''} onChange={e => updatePkg(idx, 'packages', e.target.value)} className="h-8 text-sm" data-testid={`lr-pkg-packages-${idx}`} /></td>
                      <td className="px-1 py-1"><Input value={p.description || ''} onChange={e => updatePkg(idx, 'description', e.target.value)} className="h-8 text-sm" data-testid={`lr-pkg-desc-${idx}`} /></td>
                      <td className="px-1 py-1"><Input value={p.actual_weight || ''} onChange={e => updatePkg(idx, 'actual_weight', e.target.value)} className="h-8 text-sm" data-testid={`lr-pkg-actual-${idx}`} /></td>
                      <td className="px-1 py-1"><Input value={p.charged_weight || ''} onChange={e => updatePkg(idx, 'charged_weight', e.target.value)} className="h-8 text-sm" data-testid={`lr-pkg-charged-${idx}`} /></td>
                      <td className="px-1 py-1"><Input value={p.rate || ''} onChange={e => updatePkg(idx, 'rate', e.target.value)} className="h-8 text-sm" data-testid={`lr-pkg-rate-${idx}`} /></td>
                      <td className="px-1 py-1"><Input value={p.amount || ''} onChange={e => updatePkg(idx, 'amount', e.target.value)} className="h-8 text-sm" data-testid={`lr-pkg-amount-${idx}`} /></td>
                      <td className="px-1 py-1">
                        {formData.packages.length > 1 && <button onClick={() => removePkg(idx)} className="p-1 text-red-400 hover:text-red-600" data-testid={`lr-remove-pkg-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addPkg} className="border-orange-500 text-orange-600 hover:bg-orange-50 text-xs" data-testid="lr-add-pkg-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Row
            </Button>
          </div>
        </Card>

        {/* Bottom actions */}
        <div className="flex justify-end gap-3 pb-4">
          <Button variant="outline" onClick={() => navigate(`/dockets/${docketId}/lr-receipts`)} className="border-orange-500 text-orange-600 hover:bg-orange-50" data-testid="lr-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="lr-bottom-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{saving ? 'Saving...' : 'Save LR Receipt'}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default LrReceiptForm;
